import React, { useCallback, useRef, useState } from 'react';

import { Swiper, SwiperSlide } from 'swiper/react';

import ExpertCard from '@core/components/expertcard/ExpertCard';
import ActionButton from '@core/components/actionbutton/ActionButton';

import ArrowLeftIcon from 'assets/icons/arrow-left-gray.svg';
import ArrowRightIcon from 'assets/icons/arrow-right-gray.svg';

import 'swiper/swiper-bundle.min.css';
import 'swiper/swiper.min.css';

import classes from './ContentExperts.module.scss';

/**
 * The experts behind a piece of content. Lives in common/ because both the
 * course page and the career path page render it; the title differs per page,
 * so it is a prop.
 */
const ContentExperts = ({ experts, title = 'About the author' }) => {
	const sliderRef = useRef(null);
	// Both true means every slide already fits, so there is nothing to page
	// through and the arrows are hidden rather than shown doing nothing.
	const [bounds, setBounds] = useState({ isBeginning: true, isEnd: true });

	const syncBounds = useCallback((swiper) => {
		setBounds({ isBeginning: swiper.isBeginning, isEnd: swiper.isEnd });
	}, []);

	const handlePrev = useCallback(() => {
		sliderRef.current?.swiper?.slidePrev();
	}, []);

	const handleNext = useCallback(() => {
		sliderRef.current?.swiper?.slideNext();
	}, []);

	if (!experts?.length) return null;

	const canScroll = !(bounds.isBeginning && bounds.isEnd);

	return (
		<section className={classes['wrapper']}>
			<div className={classes['header']}>
				<h3 className={classes['title']}>{title}</h3>

				{canScroll && (
					<div className={classes['arrows']}>
						<ActionButton
							kind="blank"
							onClick={handlePrev}
							disabled={bounds.isBeginning}
							aria-label="Previous experts">
							<ArrowLeftIcon />
						</ActionButton>
						<ActionButton
							kind="blank"
							onClick={handleNext}
							disabled={bounds.isEnd}
							aria-label="Next experts">
							<ArrowRightIcon />
						</ActionButton>
					</div>
				)}
			</div>

			<Swiper
				ref={sliderRef}
				// Fixed-width slides (set in SCSS) rather than a slidesPerView
				// count: this section renders in containers of different widths,
				// and the card is designed to sit at 300px in all of them.
				slidesPerView="auto"
				spaceBetween={20}
				watchOverflow
				onSwiper={syncBounds}
				onSlideChange={syncBounds}
				onResize={syncBounds}
				className={classes['slider']}>
				{experts.map((expert) => (
					<SwiperSlide key={expert.unique_string_id} className={classes['slide']}>
						<ExpertCard expert={expert} />
					</SwiperSlide>
				))}
			</Swiper>
		</section>
	);
};

export default ContentExperts;
